import React, { useState, useEffect } from 'react';
import api from '../api';
import Pagination from './Pagination';

const BulkEnrollStudents = ({ classId, onSuccess }) => {
    const [students, setStudents] = useState([]);
    const [enrolledStudents, setEnrolledStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [result, setResult] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const fetchStudents = async () => {
        try {
            const res = await api.get('/users/students');
            setStudents(res.data);
        } catch (error) {
            console.error('Error fetching students:', error);
        }
    };

    const fetchClassDetails = async () => {
        try {
            const res = await api.get(`/classes/${classId}`);
            setEnrolledStudents(res.data.students.map(s => s._id));
        } catch (error) {
            console.error('Error fetching class details:', error);
        }
    };

    useEffect(() => {
        fetchStudents();
        fetchClassDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId]);

    const handleBulkEnroll = async () => {
        if (selectedStudents.length === 0) {
            alert('Vui lòng chọn ít nhất một học sinh để thêm vào lớp');
            return;
        }

        try {
            setLoading(true);
            const response = await api.post(`/classes/${classId}/bulk-enroll`, {
                studentIds: selectedStudents
            });
            setResult(response.data);
            setSelectedStudents([]);
            fetchClassDetails(); // Refresh enrolled students
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Bulk enroll error:', error);
            alert('Lỗi: ' + (error.response?.data?.errors?.[0]?.msg || 'Không thể thêm học sinh'));
        } finally {
            setLoading(false);
        }
    };

    const toggleStudentSelection = (studentId) => {
        setSelectedStudents(prev =>
            prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
        );
    };

    const toggleSelectAll = () => {
        const availableStudents = filteredStudents
            .filter(s => !enrolledStudents.includes(s._id))
            .map(s => s._id);

        if (selectedStudents.length === availableStudents.length) {
            setSelectedStudents([]);
        } else {
            setSelectedStudents(availableStudents);
        }
    };

    const filteredStudents = students.filter(student =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const availableStudents = filteredStudents.filter(s => !enrolledStudents.includes(s._id));

    // Pagination calculations
    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentStudents = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);

    // Reset to page 1 when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return (
        <div className="rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 p-6 shadow-lg">
            <h3 className="mb-4 text-xl font-semibold text-slate-900">
                <i className="fas fa-user-plus mr-2 text-purple-600" aria-hidden></i>
                Thêm hàng loạt học sinh vào lớp
            </h3>

            {/* Search Bar */}
            <div className="mb-4 rounded-xl bg-white p-3 shadow">
                <div className="flex items-center gap-2">
                    <i className="fas fa-search text-slate-400" aria-hidden></i>
                    <input
                        type="text"
                        placeholder="Tìm kiếm học sinh theo tên hoặc email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 border-0 bg-transparent text-sm text-slate-700 focus:outline-none"
                    />
                </div>
            </div>

            {/* Student Selection Table */}
            <div className="mb-4 max-h-96 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow">
                <table className="w-full">
                    <thead className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                        <tr>
                            <th className="px-4 py-3 text-left">
                                <input
                                    type="checkbox"
                                    checked={selectedStudents.length === availableStudents.length && availableStudents.length > 0}
                                    onChange={toggleSelectAll}
                                    className="h-4 w-4 cursor-pointer rounded border-white"
                                />
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Họ tên</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Trạng thái</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                        {currentStudents.map((student) => {
                            const isEnrolled = enrolledStudents.includes(student._id);
                            const isSelected = selectedStudents.includes(student._id);

                            return (
                                <tr
                                    key={student._id}
                                    className={`transition ${
                                        isEnrolled
                                            ? 'bg-slate-50 opacity-60'
                                            : isSelected
                                            ? 'bg-purple-50'
                                            : 'hover:bg-slate-50'
                                    }`}
                                >
                                    <td className="px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleStudentSelection(student._id)}
                                            disabled={isEnrolled}
                                            className="h-4 w-4 cursor-pointer rounded border-slate-300 disabled:cursor-not-allowed"
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-slate-900">
                                        {student.name}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{student.email}</td>
                                    <td className="px-4 py-3 text-sm">
                                        {isEnrolled ? (
                                            <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                                ✓ Đã tham gia
                                            </span>
                                        ) : (
                                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                                                Chưa tham gia
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {currentStudents.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                                    {searchTerm ? 'Không tìm thấy học sinh' : 'Chưa có học sinh nào'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {filteredStudents.length > 0 && (
                <div className="mb-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={itemsPerPage}
                        totalItems={filteredStudents.length}
                    />
                </div>
            )}

            {/* Action Button */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                    Đã chọn: <span className="font-semibold text-purple-600">{selectedStudents.length}</span> học sinh
                </p>
                <button
                    onClick={handleBulkEnroll}
                    disabled={selectedStudents.length === 0 || loading}
                    className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <>
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                            Đang xử lý...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-user-plus" aria-hidden></i>
                            Thêm vào lớp ({selectedStudents.length})
                        </>
                    )}
                </button>
            </div>

            {/* Result Summary */}
            {result && (
                <div className="mt-4 rounded-xl bg-white p-4 shadow">
                    <h4 className="mb-2 font-semibold text-slate-800">
                        <i className="fas fa-check-circle mr-2 text-green-600" aria-hidden></i>
                        Kết quả thêm học sinh
                    </h4>
                    <div className="space-y-1 text-sm">
                        <p>
                            Tổng số: <span className="font-semibold">{result.summary.total}</span>
                        </p>
                        <p className="text-green-600">
                            Thành công: <span className="font-semibold">{result.summary.enrolled}</span>
                        </p>
                        <p className="text-yellow-600">
                            Đã tham gia trước: <span className="font-semibold">{result.summary.skipped}</span>
                        </p>
                        <p className="text-red-600">
                            Thất bại: <span className="font-semibold">{result.summary.failed}</span>
                        </p>
                    </div>

                    {/* Successfully Enrolled Students */}
                    {result.enrolledStudents && result.enrolledStudents.length > 0 && (
                        <div className="mt-3">
                            <p className="mb-2 text-xs font-semibold text-slate-700">Học sinh đã thêm:</p>
                            <div className="max-h-32 overflow-y-auto rounded bg-green-50 p-2">
                                <ul className="space-y-1 text-xs text-green-800">
                                    {result.enrolledStudents.map((student, index) => (
                                        <li key={index}>
                                            ✓ {student.name} ({student.email})
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Errors */}
                    {result.errors && result.errors.length > 0 && (
                        <div className="mt-3">
                            <p className="mb-2 text-xs font-semibold text-red-700">Lỗi:</p>
                            <div className="max-h-32 overflow-y-auto rounded bg-red-50 p-2">
                                <ul className="space-y-1 text-xs text-red-800">
                                    {result.errors.map((err, index) => (
                                        <li key={index}>
                                            ✗ {err.studentId}: {err.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BulkEnrollStudents;
